import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import { getBlogPost } from '../data/blogPosts';
import EmailCapture from '../components/EmailCapture';
import ShareBar from '../components/ShareBar';

const CATEGORY_COLORS = {
  'Savings':   '#38bdf8',
  'Debt':      '#ef4444',
  'Investing': '#a78bfa',
  'Crypto':    '#f59e0b',
  'Markets':   '#22c55e',
  'Credit':    '#f472b6',
};

function renderBlock(block, i) {
  switch (block.type) {
    case 'intro':
      return <p key={i} style={s.intro}>{block.text}</p>;
    case 'h2':
      return <h2 key={i} style={s.h2}>{block.text}</h2>;
    case 'p':
      return <p key={i} style={s.p}>{block.text}</p>;
    case 'list':
      return (
        <ul key={i} style={s.list}>
          {block.items.map((item, j) => <li key={j} style={s.listItem}>{item}</li>)}
        </ul>
      );
    case 'callout':
      return <div key={i} style={s.callout}>{block.text}</div>;
    case 'tip':
      return <div key={i} style={s.tip}>{block.text}</div>;
    case 'warning':
      return <div key={i} style={s.warning}>{block.text}</div>;
    case 'feature_link':
      return (
        <Link key={i} to={block.to} style={s.featureLink}>
          <span style={s.featureLinkIcon}>{block.icon}</span>
          <div style={s.featureLinkBody}>
            <div style={s.featureLinkLabel}>{block.label} →</div>
            {block.desc && <div style={s.featureLinkDesc}>{block.desc}</div>}
          </div>
        </Link>
      );
    case 'faq':
      return (
        <div key={i} style={s.faqItem}>
          <div style={s.faqQ}>Q: {block.question}</div>
          <div style={s.faqA}>{block.answer}</div>
        </div>
      );
    default:
      return null;
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getBlogPost(slug);

  if (!post) {
    return (
      <div style={s.page}>
        <div style={s.inner}>
          <h1 style={s.h1}>Article not found</h1>
          <Link to="/blog" style={s.backLink}>← Back to all guides</Link>
        </div>
      </div>
    );
  }

  const catColor = CATEGORY_COLORS[post.category] || '#64748b';

  // Build FAQ JSON-LD schema from faq blocks
  const faqBlocks = (post.content || []).filter(b => b.type === 'faq');
  const faqSchema = faqBlocks.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqBlocks.map(b => ({
      '@type': 'Question',
      name: b.question,
      acceptedAnswer: { '@type': 'Answer', text: b.answer },
    })),
  } : null;

  // Article JSON-LD schema
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.description,
    author: { '@type': 'Person', name: post.author || 'KnowFi Editorial Team' },
    publisher: { '@type': 'Organization', name: 'KnowFi', url: 'https://knowfi.io' },
    datePublished: post.date,
    dateModified: post.date,
  };

  return (
    <div style={s.page}>
      <SEO
        title={post.title}
        description={post.description}
        path={`/blog/${post.slug}`}
        type="article"
        keywords={`${post.category}, personal finance, ${post.title.toLowerCase()}, KnowFi`}
        publishedDate={post.date}
        author={post.author || 'KnowFi Editorial Team'}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      </Helmet>
      <div style={s.glow} />
      <div style={s.inner}>

        {/* Back nav */}
        <Link to="/blog" style={s.backLink}>← All guides</Link>

        {/* Header */}
        <div style={s.articleHeader}>
          <div style={s.metaRow}>
            <span style={{ ...s.catBadge, background: catColor + '18', color: catColor, border: `1px solid ${catColor}33` }}>
              {post.category}
            </span>
            <span style={s.readTime}>{post.readTime} read · {post.date}</span>
          </div>
          <div style={s.emoji}>{post.emoji}</div>
          <h1 style={s.h1}>{post.title}</h1>
          <p style={s.description}>{post.description}</p>
          <p style={s.authorLine}>✍️ By {post.author || 'KnowFi Editorial Team'}</p>

          {/* Share bar — top */}
          <div style={{ marginTop: '20px' }}>
            <ShareBar
              text={`${post.title} — ${post.description}`}
              url={`https://knowfi.io/blog/${post.slug}`}
            />
          </div>
        </div>

        {/* Article body */}
        <div style={s.body}>
          {post.content.map((block, i) => renderBlock(block, i))}
        </div>

        {/* Share bar — bottom */}
        <div style={{ margin: '32px 0 8px' }}>
          <ShareBar
            text={`${post.title} — ${post.description}`}
            url={`https://knowfi.io/blog/${post.slug}`}
          />
        </div>

        {/* CTA section */}
        <div style={s.ctaSection}>
          <h3 style={s.ctaTitle}>Put this into action with KnowFi</h3>
          <div style={s.ctaGrid}>

            <Link to="/tracker" style={s.ctaCard}>
              <div style={s.ctaCardIcon}>📊</div>
              <div style={s.ctaCardTitle}>Build Your Free Dashboard</div>
              <div style={s.ctaCardDesc}>Track your income, debt, savings, and investments in one place. See your progress every month.</div>
              <div style={s.ctaCardLink}>Start tracking free →</div>
            </Link>

            <Link to="/gameplan" style={s.ctaCard}>
              <div style={s.ctaCardIcon}>🎯</div>
              <div style={s.ctaCardTitle}>Build My Money Game Plan</div>
              <div style={s.ctaCardDesc}>Answer 4 questions and get a personalized step-by-step financial plan built around your actual numbers.</div>
              <div style={s.ctaCardLink}>Get my free plan →</div>
            </Link>

            <Link to="/brief" style={{ ...s.ctaCard, ...s.ctaCardAmber }}>
              <div style={s.ctaCardIcon}>📰</div>
              <div style={s.ctaCardTitle}>The KnowFi Brief</div>
              <div style={s.ctaCardDesc}>Daily market updates in plain English. What happened, why it happened, and what it means for your money.</div>
              <div style={{ ...s.ctaCardLink, color: '#f59e0b' }}>Read today's brief →</div>
            </Link>

          </div>
        </div>

        {/* More articles */}
        <div style={s.moreSection}>
          <div style={s.moreTitle}>More guides</div>
          <Link to="/blog" style={s.moreLink}>← Browse all free guides</Link>
        </div>

        {/* Financial disclaimer */}
        <div style={s.disclaimer}>
          <strong>Disclaimer:</strong> This content is for educational purposes only and is not financial, investment, or tax advice. Always consult a qualified financial professional before making financial decisions.
        </div>

      {/* Email capture — end of article */}
      <div style={{ marginTop: '40px' }}>
        <EmailCapture variant="card" source={`blog-${post.slug}`} title="Enjoyed this? Get the daily brief." subtitle="Plain English market + money news every morning. Free." />
      </div>

      
  {/* AEO Answer Blocks — for AI/LLM citability */}
  {post.aeoAnswers && post.aeoAnswers.length > 0 && (
    <div style={{ marginTop: '32px', marginBottom: '32px' }}>
      <div style={{ fontSize: '12px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
        Quick Answers
      </div>
      {post.aeoAnswers.map((item, index) => (
        <div key={index} style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '15px', fontWeight: '700', color: '#f1f5f9', marginBottom: '6px' }}>
            {item.q}
          </div>
          <div className="aeo-answer">
            {item.a}
          </div>
        </div>
      ))}
    </div>
  )}
  </div>
    </div>
  );
}

const s = {
  page:  { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: '40px 24px 80px', position: 'relative', overflow: 'hidden' },
  glow:  { position: 'absolute', top: '-60px', left: '50%', transform: 'translateX(-50%)', width: '700px', height: '500px', background: 'radial-gradient(ellipse at center, rgba(56,189,248,0.07) 0%, transparent 65%)', pointerEvents: 'none' },
  inner: { maxWidth: '760px', margin: '0 auto', position: 'relative', zIndex: 1 },

  backLink: { color: '#475569', textDecoration: 'none', fontSize: '14px', display: 'inline-block', marginBottom: '32px' },

  articleHeader: { marginBottom: '40px' },
  metaRow:  { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  catBadge: { fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.3px' },
  readTime: { fontSize: '13px', color: '#475569' },
  emoji:    { fontSize: '48px', lineHeight: 1, marginBottom: '16px' },
  h1:       { fontSize: 'clamp(26px,4vw,40px)', fontWeight: '900', margin: '0 0 14px', lineHeight: 1.2, letterSpacing: '-1px', color: '#f1f5f9' },
  description: { color: '#64748b', fontSize: '17px', lineHeight: 1.65, margin: '0 0 12px' },
  authorLine:  { fontSize: '13px', color: '#475569', margin: 0 },

  body: { borderTop: '1px solid #1e293b', paddingTop: '36px', marginBottom: '48px' },

  intro:    { fontSize: '17px', color: '#94a3b8', lineHeight: 1.75, marginBottom: '28px', fontStyle: 'italic', borderLeft: '3px solid #334155', paddingLeft: '16px' },
  h2:       { fontSize: '22px', fontWeight: '800', color: '#f1f5f9', margin: '36px 0 14px', letterSpacing: '-0.3px' },
  p:        { fontSize: '16px', color: '#94a3b8', lineHeight: 1.75, margin: '0 0 18px' },
  list:     { margin: '0 0 18px', paddingLeft: '0', listStyle: 'none' },
  listItem: { fontSize: '15px', color: '#94a3b8', lineHeight: 1.65, padding: '8px 0 8px 20px', borderBottom: '1px solid #1e293b', position: 'relative' },
  callout:  { background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.2)', borderRadius: '12px', padding: '16px 20px', margin: '20px 0', fontSize: '15px', color: '#94a3b8', lineHeight: 1.65 },
  tip:      { background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: '12px', padding: '16px 20px', margin: '20px 0', fontSize: '15px', color: '#94a3b8', lineHeight: 1.65 },
  warning:  { background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '12px', padding: '16px 20px', margin: '20px 0', fontSize: '15px', color: '#94a3b8', lineHeight: 1.65 },

  // CTA section
  ctaSection: { background: '#1e293b', borderRadius: '20px', padding: '32px', border: '1px solid #334155', marginBottom: '40px' },
  ctaTitle:   { fontSize: '20px', fontWeight: '800', color: '#f1f5f9', margin: '0 0 20px', letterSpacing: '-0.3px' },
  ctaGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '14px' },
  ctaCard:    { background: '#0f172a', borderRadius: '14px', padding: '18px', border: '1px solid #334155', textDecoration: 'none', color: '#f8fafc', display: 'flex', flexDirection: 'column', gap: '8px' },
  ctaCardAmber: { border: '1px solid rgba(245,158,11,0.2)', background: 'rgba(245,158,11,0.04)' },
  ctaCardIcon:  { fontSize: '24px' },
  ctaCardTitle: { fontSize: '14px', fontWeight: '700', color: '#f1f5f9' },
  ctaCardDesc:  { fontSize: '12px', color: '#475569', lineHeight: 1.55, flex: 1 },
  ctaCardLink:  { fontSize: '12px', color: '#38bdf8', fontWeight: '600', marginTop: '4px' },

  moreSection: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '24px', borderTop: '1px solid #1e293b' },
  moreTitle:   { color: '#475569', fontSize: '14px' },
  moreLink:    { color: '#38bdf8', textDecoration: 'none', fontSize: '14px', fontWeight: '600' },

  // Feature link block (internal linking)
  featureLink:     { display: 'flex', alignItems: 'center', gap: '14px', background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.18)', borderRadius: '12px', padding: '14px 18px', margin: '20px 0', textDecoration: 'none', color: '#f8fafc', transition: 'background 0.2s' },
  featureLinkIcon: { fontSize: '24px', flexShrink: 0 },
  featureLinkBody: { display: 'flex', flexDirection: 'column', gap: '3px' },
  featureLinkLabel:{ fontSize: '14px', fontWeight: '700', color: '#38bdf8' },
  featureLinkDesc: { fontSize: '13px', color: '#64748b', lineHeight: 1.45 },

  // FAQ block
  faqItem: { background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '12px', padding: '16px 20px', margin: '16px 0' },
  faqQ:    { fontSize: '15px', fontWeight: '700', color: '#f1f5f9', marginBottom: '8px' },
  faqA:    { fontSize: '14px', color: '#94a3b8', lineHeight: 1.65 },

  // Financial disclaimer
  disclaimer: { marginTop: '40px', padding: '14px 18px', background: 'rgba(100,116,139,0.06)', border: '1px solid #1e293b', borderRadius: '10px', fontSize: '12px', color: '#475569', lineHeight: 1.6 },
};
